import { FC, useState, useRef, useMemo, useEffect } from 'react';
import 'handsontable/dist/handsontable.full.css';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import { successToast, errorToast } from '@/helpers';
import { ApiService } from '@/support/ApiService.ts';
import InVivoError from '@/model/InVivoError.ts';
import { ExceptionHandler } from '@/utils/ExceptionHandler.ts';
import { _notNil, _isNotEmpty, _isEmptyString, _isNil, _isNotBlank } from '@/littledash';
import {
  validateSampleTypeValue,
  validateSampleTypeLabel,
  getBasicSampleTypeErrorMessage,
  getBasicLabelErrorMessage,
  ValidationData,
} from '@/utils/sampleTypeValidation';
import type { SampleTypeRow } from '@/model/SampleType.model';
import { registerHOTModules } from '@/support/hot';
import SpreadSheet from '@/components/UI/SpreadSheet';
import { MultiSelectEditor } from '@/components/UI/SpreadSheet/editors/MultiSelectEditor';

registerHOTModules();
MultiSelectEditor.register();

const AVAILABLE_DETAIL_OPTIONS: Array<string> = [
  'Weight (mg g µg)',
  'Volume (ml µl cm³ mm³)',
  'Width (mm)',
  'Length (mm)',
  'Depth (mm)',
];

const DISPLAY_TO_API_MAP: Record<string, string> = {
  'Weight (mg g µg)': 'weight',
  'Volume (ml µl cm³ mm³)': 'volume',
  'Width (mm)': 'width',
  'Length (mm)': 'length',
  'Depth (mm)': 'depth',
};

interface BulkAddSampleTypesProps {
  onSave: () => void;
  onCancel: () => void;
}

const BulkAddSampleTypes: FC<BulkAddSampleTypesProps> = ({ onSave, onCancel }) => {
  const [saving, setSaving] = useState(false);
  const [validationData, setValidationData] = useState<ValidationData | null>(null);
  const [loadingValidation, setLoadingValidation] = useState(true);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);
  const hotTableRef = useRef<any>(null);

  useEffect(() => {
    const fetchValidationData = async (): Promise<void> => {
      try {
        const response = await ApiService.call({
          endpoint: 'GET /api/v1/team-glossary/sample-validation-data',
          path: undefined,
          options: {
            onError: { toast: false, throw: true, capture: false, slug: 'bulk-add-sample-types-validation-load' },
          },
        });
        setValidationData(response.body as ValidationData);
      } catch (error) {
        errorToast('Failed to load validation data');
        ExceptionHandler.captureException(
          new InVivoError('Failed to load validation data', {
            cause: error,
            slug: 'bulk-add-sample-types-validation-load',
          })
        );
        setValidationData(null);
      } finally {
        setLoadingValidation(false);
      }
    };

    void fetchValidationData();
  }, []);

  const validateSampleType = (value: string, rowIndex: number): { isValid: boolean; message?: string } => {
    const hotInstance = hotTableRef.current?.hotInstance;
    const spreadsheetData = hotInstance ? (hotInstance.getData() as Array<SampleTypeRow>) : [];

    return validateSampleTypeValue(value, rowIndex, validationData, [], spreadsheetData);
  };

  const validateLabel = (value: string, rowIndex: number): { isValid: boolean; message?: string } => {
    const hotInstance = hotTableRef.current?.hotInstance;
    const spreadsheetData = hotInstance ? (hotInstance.getData() as Array<SampleTypeRow>) : [];

    // For BulkAdd, we don't have existing sample types, so pass empty array
    return validateSampleTypeLabel(value, rowIndex, validationData, [], spreadsheetData);
  };

  // Start with one empty row
  const initialData: Array<SampleTypeRow | []> = useMemo(() => {
    return [['', '', ''], []];
  }, []);

  const columns = useMemo(
    () => [
      {
        data: 0,
        title: 'Sample type',
        type: 'text',
        className: 'htMiddle',
        width: 140,
        allowInvalid: true,
        validator: function (this: any, value: unknown, callback: (valid: boolean) => void) {
          if (_isNil(value) || typeof value !== 'string') {
            callback(false);
            return;
          }

          const rowIndex = this.row;
          if (typeof rowIndex !== 'number') {
            callback(true);
            return;
          }

          const validation = validateSampleType(value, rowIndex);
          callback(validation.isValid);
        },
        validationErrorMessage: (value: unknown) => {
          return getBasicSampleTypeErrorMessage(value as string, validationData);
        },
      },
      {
        data: 1,
        title: 'Label',
        type: 'text',
        className: 'htMiddle',
        width: 120,
        allowInvalid: true,
        validator: function (this: any, value: unknown, callback: (valid: boolean) => void) {
          if (_isNil(value)) {
            callback(true);
            return;
          }

          if (typeof value !== 'string') {
            callback(false);
            return;
          }

          const rowIndex = this.row;
          if (typeof rowIndex !== 'number') {
            callback(true);
            return;
          }

          const validation = validateLabel(value, rowIndex);
          callback(validation.isValid);
        },
        validationErrorMessage: (value: unknown) => {
          return getBasicLabelErrorMessage(value as string, validationData);
        },
      },

      {
        data: 2,
        title: 'Details',
        type: 'multi-select',
        className: 'htMiddle',
        width: 280,
        options: AVAILABLE_DETAIL_OPTIONS,
        isMultiSelect: true,
        jsonEncoded: false,
        allowInvalid: true,
        validator: (value: unknown, callback: (valid: boolean) => void) => {
          if (_isNil(value) || value === '') {
            callback(true);
            return;
          }

          const selectedOptions =
            typeof value === 'string'
              ? value
                  .split(',')
                  .map((v) => v.trim())
                  .filter(Boolean)
              : [];

          const allValid = selectedOptions.every((option) => AVAILABLE_DETAIL_OPTIONS.includes(option));
          callback(allValid);
        },
      },
    ],
    [validationData]
  );

  const hotTableSettings = useMemo(
    () => ({
      data: initialData,
      colHeaders: ['Sample type', 'Label', 'Details'],
      columns,
      rowHeaders: true,
      manualColumnResize: true,
      contextMenu: {
        items: {
          row_above: {},
          row_below: {},
          remove_row: {},
        },
      },
      fillHandle: false,
      outsideClickDeselects: false,
      width: '100%',
      height: 400,
      rowHeights: 50,
      className: 'htMiddle',
      licenseKey: AppConfig.handsOnTableLicence,
      stretchH: 'all' as 'none' | 'all' | 'last' | undefined,
      minRows: 1,
      minSpareRows: 1,
      allowInsertRow: true,
    }),
    [initialData, columns]
  );

  const handleSave = async () => {
    const hotInstance = hotTableRef.current?.hotInstance;
    if (_isNil(hotInstance)) {
      errorToast('Unable to access table data');
      return;
    }

    setSaving(true);

    try {
      const currentData = (hotInstance.getData?.() ?? []) as Array<SampleTypeRow>;

      const validRows = currentData.filter((row) => {
        const [title] = row;
        return _isNotBlank(title);
      });

      if (validRows.length === 0) {
        errorToast('Please add at least one sample type');
        setSaving(false);
        return;
      }

      const teamGlossariesPayload = validRows.map((row) => {
        const [title, label, detailsRaw] = row;

        const detailsDisplay = Array.isArray(detailsRaw)
          ? detailsRaw.map((d) => String(d).trim()).filter(Boolean)
          : detailsRaw
            ? String(detailsRaw)
                .split(',')
                .map((d) => d.trim())
                .filter(Boolean)
            : [];

        const detailsApi = detailsDisplay
          .map((d) => DISPLAY_TO_API_MAP[d])
          .filter(
            (d): d is 'volume' | 'weight' | 'width' | 'length' | 'depth' =>
              d !== undefined && ['volume', 'weight', 'width', 'length', 'depth'].includes(d)
          );

        const maybeTitle = (_notNil(title) ? title : '').toString().trim();
        const maybeLabel = (_notNil(label) ? label : '').toString().trim();

        return {
          title: maybeTitle,
          type: 'none' as const,
          description: null,
          group: 'samples' as const,
          options: _isNotEmpty(detailsApi) ? { details: detailsApi } : null,
          ...(_notNil(maybeLabel) && !_isEmptyString(maybeLabel) && { label: maybeLabel }),
        };
      });

      await ApiService.call({
        endpoint: 'POST /api/v1/team-glossary',
        path: undefined,
        body: { team_glossaries: teamGlossariesPayload },
        options: { onError: { toast: false, throw: true, capture: false, slug: 'bulk-add-sample-types-save' } },
      });

      successToast(`Successfully added ${validRows.length} sample type${validRows.length !== 1 ? 's' : ''}`);
      onSave();
    } catch (error) {
      errorToast('Failed to create sample types');
      ExceptionHandler.captureException(
        new InVivoError('Failed to create sample types', {
          cause: error,
          slug: 'bulk-add-sample-types-save',
        })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalContainer className="bg-white">
      <ModalHeader title="Create" closeModal={onCancel} closeDisabled={saving} className="pa3 bb b--moon-gray w-100" />

      <div className="pa3">
        {loadingValidation ? (
          <div className="tc pa4">
            <div className="mb2">Loading validation data...</div>
          </div>
        ) : (
          <div className="mv3 w-100 overflow-auto ow-spreadsheet-styles">
            <SpreadSheet
              className={'mt3'}
              data={initialData}
              settings={hotTableSettings}
              addCol={false}
              innerRef={hotTableRef}
              autoAddRow
              enableRowHeaderControls
              setInvalid={setHasValidationErrors}
            />
          </div>
        )}
      </div>

      <ModalActions
        className="pa3 bt b--moon-gray w-100"
        onSubmit={handleSave}
        onCancel={onCancel}
        submitBtnText="Create"
        cancelBtnText="Cancel"
        submitButtonProps={{
          loading: saving,
          disabled: saving || loadingValidation || hasValidationErrors,
        }}
        cancelButtonProps={{
          disabled: saving,
        }}
      />
    </ModalContainer>
  );
};

export default BulkAddSampleTypes;
